import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * SEO component for managing document head tags
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.canonicalUrl - Optional canonical URL override
 * @param {Object} props.openGraph - Optional Open Graph data
 */
const SEO = ({ 
  title, 
  description, 
  canonicalUrl,
  openGraph = {}
}) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://iloveoph.com';
  
  // Build canonical URL - either use provided one or build from current path
  let canonical = canonicalUrl || location.pathname;
  
  // Ensure canonical URL is absolute
  if (canonical && !canonical.startsWith('http')) {
    canonical = `${siteUrl}${canonical.startsWith('/') ? '' : '/'}${canonical}`;
  }
  
  // If we have a search param for a menu item, generate a clean canonical URL
  const searchParams = new URLSearchParams(location.search);
  const itemSlug = searchParams.get('item');
  
  // For menu items, create a canonical URL to the clean path
  let finalCanonical = canonical;
  if (itemSlug && location.pathname.includes('/menu')) {
    // Get the category from the URL
    const pathParts = location.pathname.split('/');
    const categorySlug = pathParts.length >= 3 && pathParts[1] === 'menu' ? pathParts[2] : 'all';
    
    // Create a canonical URL to the clean path
    finalCanonical = `${siteUrl}/menu/${categorySlug}/${itemSlug}`;
  }
  
  // Default fallback image
  const defaultImage = `${siteUrl}/oph-social-share1.jpg`;
  
  // Ensure image is an absolute URL
  let imageUrl = openGraph.image || defaultImage;
  if (imageUrl && !imageUrl.startsWith('http')) {
    imageUrl = `${siteUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  }
  
  // Default Open Graph data
  const defaultOpenGraph = {
    title: title,
    description: description,
    url: finalCanonical,
    type: 'website',
    site_name: 'The Original Pancake House',
    image: imageUrl,
    imageWidth: '1200',
    imageHeight: '630',
    ...openGraph
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={finalCanonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={defaultOpenGraph.type} />
      <meta property="og:url" content={defaultOpenGraph.url} />
      <meta property="og:title" content={defaultOpenGraph.title} />
      <meta property="og:description" content={defaultOpenGraph.description} />
      <meta property="og:site_name" content={defaultOpenGraph.site_name} />
      <meta property="og:image" content={defaultOpenGraph.image} />
      <meta property="og:image:width" content={defaultOpenGraph.imageWidth} />
      <meta property="og:image:height" content={defaultOpenGraph.imageHeight} />
      <meta property="og:image:alt" content={title} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={defaultOpenGraph.title} />
      <meta name="twitter:description" content={defaultOpenGraph.description} />
      <meta name="twitter:image" content={defaultOpenGraph.image} />
      <meta name="twitter:image:alt" content={title} />
    </Helmet>
  );
};

export default SEO;
